import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { LABELS } from "@match/shared";
import { PageHeader } from "@/components/Layout";
import { DD_APPROXIMATE_EXPLANATION, FeesPaid, OutcomePanel, PHASE_LABEL, Progress, SessionStateLabel } from "@/components/session";
import { ClosePairButton, SessionTrade } from "@/components/trade";
import { Auto, Button, Dash, ErrorNote, Field, inputClass, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { dateTime, lots, money, percent, signClass } from "@/lib/format";
import { useRefreshDirectory, useSession } from "@/lib/queries";
import { decimalInput, fractionToPct, intInput, pctToFraction } from "@/lib/units";
import type { HedgeLeg, SessionDetail as Detail } from "@/lib/types";

const TABS = [
  { key: "operazione", label: "Operazione" },
  { key: "risultato", label: "Risultato" },
  { key: "operazioni", label: "Operazioni" },
  { key: "parametri", label: "Parametri" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

export function SessionDetail() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get("tab"))?.key ?? "operazione") as TabKey;
  const session = useSession(id);
  const canWrite = useCanWrite();
  const [outcome, setOutcome] = useState<"complete" | "fail" | null>(null);

  if (session.isLoading) return <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>;
  if (session.error || !session.data) {
    return (
      <div className="px-6 py-6">
        <ErrorNote>{session.error ? errorMessage(session.error) : "Fase non trovata"}</ErrorNote>
        <Link to="/fasi" className="mt-3 inline-block text-sm text-muted hover:text-ink">
          Torna alle fasi
        </Link>
      </div>
    );
  }

  const s = session.data;
  const live = s.state === "ACTIVE" || s.state === "PAUSED";

  return (
    <>
      <PageHeader
        title={`${s.cycle.client.name} · ${PHASE_LABEL(s.phase)}`}
        subtitle={`${s.cycle.preset.propFirm.name} ${s.cycle.preset.name} · ${s.symbols?.prop.brokerSymbol ?? s.canonicalSymbol} contro ${s.symbols?.broker.brokerSymbol ?? s.canonicalSymbol}`}
        actions={
          <div className="flex items-center gap-2">
            <SessionStateLabel state={s.state} />
            {canWrite && live && (
              <>
                <PauseButton s={s} />
                <Button onClick={() => setOutcome("complete")}>{LABELS.sessionState.COMPLETED}</Button>
                <Button onClick={() => setOutcome("fail")}>{LABELS.sessionState.FAILED}</Button>
              </>
            )}
          </div>
        }
      />

      <nav className="flex gap-1 border-b border-line px-6" aria-label="Sezioni della fase">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setParams(t.key === "operazione" ? {} : { tab: t.key }, { replace: true })}
            aria-current={t.key === tab ? "page" : undefined}
            className={[
              "-mb-px border-b-2 px-3 py-2.5 text-sm transition-colors",
              t.key === tab ? "border-ink font-medium text-ink" : "border-transparent text-muted hover:text-ink",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="px-6 py-5">
        {tab === "operazione" && <SessionTrade s={s} canWrite={canWrite} />}
        {tab === "risultato" && <ResultTab s={s} />}
        {tab === "operazioni" && <PairsTab s={s} canWrite={canWrite} />}
        {tab === "parametri" && <ParamsTab s={s} canWrite={canWrite} onOutcome={setOutcome} />}
      </div>

      {outcome && (
        <OutcomePanel
          kind={outcome}
          session={{
            id: s.id,
            clientName: s.cycle.client.name,
            phase: s.cycle.currentPhase,
            presetPhases: s.cycle.preset.phases,
            brokerPnl: s.summary.pnl.broker,
            brokerCurrency: s.brokerAccount.currency,
            openPairs: s.summary.openPairs,
            reportingCurrency: s.reportingCurrency,
            propCurrency: s.propAccount.currency,
          }}
          onClose={() => setOutcome(null)}
        />
      )}
    </>
  );
}

function PauseButton({ s }: { s: Detail }) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <span title={error ?? undefined}>
      <Button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            if (s.state === "ACTIVE") await api.sessions.pause(s.id);
            else await api.sessions.resume(s.id);
            await refresh();
          } catch (e) {
            setError(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {s.state === "ACTIVE" ? "Pausa" : "Riprendi"}
      </Button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Risultato
// ---------------------------------------------------------------------------

function ResultTab({ s }: { s: Detail }) {
  const sum = s.summary;
  const propCur = s.propAccount.currency;
  const brokerCur = s.brokerAccount.currency;
  const sameCur = propCur === brokerCur;
  const cell = (n: number | null, cur: string) => (n === null ? <Dash title="Valute diverse: non si sommano senza cambio" /> : <span className={signClass(n)}>{money(n, cur)}</span>);

  return (
    <div className="space-y-5">
      <div className="overflow-x-auto">
        <table className="w-full max-w-3xl text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs text-faint">
              <th className="py-2 pr-4 font-medium" />
              <th className="py-2 pr-4 text-right font-medium text-prop">Prop</th>
              <th className="py-2 pr-4 text-right font-medium text-broker">Broker</th>
              <th className="py-2 text-right font-medium">Totale</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-line/60">
              <th className="py-2.5 pr-4 text-left font-normal text-muted">Volume</th>
              <td className="py-2.5 pr-4 text-right">{lots(sum.volume.prop)}</td>
              <td className="py-2.5 pr-4 text-right">{lots(sum.volume.broker)}</td>
              <td className="py-2.5 text-right">
                <Dash title="I lotti delle due gambe non si sommano" />
              </td>
            </tr>
            <tr className="border-b border-line/60">
              <th className="py-2.5 pr-4 text-left font-normal text-muted">P&L</th>
              <td className="py-2.5 pr-4 text-right">{cell(sum.pnl.prop, propCur)}</td>
              <td className="py-2.5 pr-4 text-right">{cell(sum.pnl.broker, brokerCur)}</td>
              <td className="py-2.5 text-right">{cell(sum.pnl.total, propCur)}</td>
            </tr>
            <tr className="border-b border-line/60">
              <th className="py-2.5 pr-4 text-left font-normal text-muted">P&L di oggi</th>
              <td className="py-2.5 pr-4 text-right">{cell(sum.dailyPnl.prop, propCur)}</td>
              <td className="py-2.5 pr-4 text-right">{cell(sum.dailyPnl.broker, brokerCur)}</td>
              <td className="py-2.5 text-right">{cell(sum.dailyPnl.total, propCur)}</td>
            </tr>
          </tbody>
        </table>
        {!sameCur && <p className="mt-2 text-xs text-faint">Prop in {propCur}, broker in {brokerCur}: il totale richiede un cambio e non viene mostrato.</p>}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Metric title="DD residuo" value={`${s.ddUsedIsApproximate ? "≈ " : ""}${money(sum.ddRemaining, propCur)}`}>
          <Progress
            label="DD usato"
            tone="prop"
            approximate={s.ddUsedIsApproximate}
            ratio={sum.ddUsedRatio}
            detail={`${money(sum.ddUsed, propCur)} di ${money(sum.maxDdMoney, propCur)}`}
          />
          {s.ddUsedIsApproximate && (
            <p className="mt-1.5 text-xs text-faint">
              Base {LABELS.ddBasis[s.cycle.maxDdBasis].toLowerCase()}: {DD_APPROXIMATE_EXPLANATION.charAt(0).toLowerCase() + DD_APPROXIMATE_EXPLANATION.slice(1)}
            </p>
          )}
        </Metric>
        <Metric title="DD giornaliero residuo" value={sum.dailyDdRemaining === null ? "—" : money(sum.dailyDdRemaining, propCur)}>
          <p className="text-xs text-faint">{sum.dailyDdMoney === null ? "La prop non prevede un DD giornaliero." : `su ${money(sum.dailyDdMoney, propCur)} al giorno, fuso del server prop`}</p>
        </Metric>
        <Metric title="Target residuo" value={sum.targetRemaining === null ? "—" : money(sum.targetRemaining, propCur)}>
          <Progress
            label="Target"
            ratio={sum.targetRatio}
            detail={sum.targetMoney === null ? "—" : `${percent(s.challengeTargetPct)} = ${money(sum.targetMoney, propCur)}`}
          />
        </Metric>
        {/* Il recover è nella valuta del ciclo (conto prop): P&L del reale convertito coppia per coppia al tasso congelato. */}
        <Metric title={LABELS.terms.recoverAmount} value={`${sum.costsRecoveredFxAssumed ? "≈ " : ""}${money(sum.recoverRemaining, s.cycle.currency)}`}>
          <Progress
            label={LABELS.terms.costsRecovered}
            tone="broker"
            approximate={sum.costsRecoveredFxAssumed}
            approximateTitle={`Una coppia chiusa senza tasso ${brokerCur}->${s.cycle.currency} è entrata a tasso 1: il numero si corregge quando la fase si chiude e congela il tasso.`}
            ratio={sum.costsRecoveredRatio}
            detail={`${money(sum.costsRecovered, s.cycle.currency)} di ${money(sum.recoverTarget, s.cycle.currency)}`}
          />
        </Metric>
      </div>
    </div>
  );
}

function Metric({ title, value, children }: { title: string; value: string; children: ReactNode }) {
  return (
    <div className="card p-4">
      <p className="text-xs text-faint">{title}</p>
      <p className="mt-0.5 text-xl font-semibold text-ink">{value}</p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Operazioni
// ---------------------------------------------------------------------------

function PairsTab({ s, canWrite }: { s: Detail; canWrite: boolean }) {
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const live = s.state === "ACTIVE" || s.state === "PAUSED";

  if (!s.pairs.length) {
    return <p className="text-sm text-muted">Nessuna operazione. Apri la prima dalla scheda Operazione: comparirà qui con lotti, orari e P&L delle due gambe.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <p className="mb-3 text-xs text-faint">
        P&L effettivo = P&L del gateway + swap + commissioni + rettifica. Il dato del gateway (<span className="italic">in corsivo</span>) non si modifica mai: la rettifica si somma.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs text-faint">
            <th className="py-2 pr-4 font-medium">#</th>
            <th className="py-2 pr-4 font-medium">Stato</th>
            <th className="py-2 pr-4 font-medium">Direzione prop</th>
            <th className="py-2 pr-4 text-right font-medium text-prop">Lotti prop</th>
            <th className="py-2 pr-4 text-right font-medium text-broker">Lotti broker</th>
            <th className="py-2 pr-4 font-medium">Aperta</th>
            <th className="py-2 pr-4 font-medium">Chiusa</th>
            <th className="py-2 pr-4 text-right font-medium text-prop">P&L prop</th>
            <th className="py-2 pr-4 text-right font-medium text-broker">P&L broker</th>
            <th className="py-2 pr-4 font-medium">Causa di chiusura</th>
            <th className="py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {s.pairs.map((p) => {
            const prop = p.legs.find((l) => l.accountRole === "PROP");
            const broker = p.legs.find((l) => l.accountRole === "BROKER");
            const canAdjust = canWrite && live && p.state === "CLOSED";
            return (
              <tr key={p.id} className="border-b border-line/60 align-top">
                <td className="py-2.5 pr-4 text-muted">{p.seq}</td>
                <td className="py-2.5 pr-4">{LABELS.pairState[p.state]}</td>
                <td className="py-2.5 pr-4">{p.propDirection === "LONG" ? "Long" : "Short"}</td>
                <td className="py-2.5 pr-4 text-right">{lots(p.propLots)}</td>
                <td className="py-2.5 pr-4 text-right">{lots(p.brokerLots)}</td>
                <td className="py-2.5 pr-4 text-muted">{dateTime(p.openedAt)}</td>
                <td className="py-2.5 pr-4 text-muted">{dateTime(p.closedAt)}</td>
                <td className="py-2.5 pr-4 text-right">
                  <LegPnl s={s} leg={prop} adjusting={adjusting} setAdjusting={setAdjusting} canAdjust={canAdjust} />
                </td>
                <td className="py-2.5 pr-4 text-right">
                  <LegPnl s={s} leg={broker} adjusting={adjusting} setAdjusting={setAdjusting} canAdjust={canAdjust} />
                </td>
                <td className="py-2.5 pr-4 text-muted">{p.closeReason ? LABELS.closeReason[p.closeReason] : <Dash />}</td>
                <td className="py-2.5 text-right">{canWrite && ["PENDING", "BOTH_OPEN", "ONE_LEG"].includes(p.state) && <ClosePairButton sessionId={s.id} pair={p} size="sm" />}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LegPnl({
  s,
  leg,
  adjusting,
  setAdjusting,
  canAdjust,
}: {
  s: Detail;
  leg: HedgeLeg | undefined;
  adjusting: string | null;
  setAdjusting: (id: string | null) => void;
  canAdjust: boolean;
}) {
  const refresh = useRefreshDirectory();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (leg && adjusting === leg.id) {
      setAmount(String(Number(leg.pnlAdjustment)));
      setReason("");
      setError(null);
    }
  }, [adjusting, leg]);

  if (!leg) return <Dash />;
  if (leg.pnlEffective === null) return <Dash title="Gamba non chiusa" />;
  const adj = Number(leg.pnlAdjustment);

  if (adjusting === leg.id) {
    const save = async () => {
      const negative = amount.trim().startsWith("-");
      const v = decimalInput(amount.replace(/^-/, ""));
      if (v === null) return setError("Scrivi un importo, per esempio -12.50");
      if (reason.trim().length < 3) return setError("Scrivi il motivo");
      setBusy(true);
      setError(null);
      try {
        await api.sessions.adjustLeg(s.id, leg.id, { pnlAdjustment: negative ? `-${v}` : v, reason: reason.trim() });
        await refresh();
        setAdjusting(null);
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setBusy(false);
      }
    };
    return (
      <div className="ml-auto w-56 space-y-1.5 text-left">
        <Field label="Rettifica totale della gamba">
          {(id) => <input id={id} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={`${inputClass} py-1`} />}
        </Field>
        <input aria-label="Motivo" placeholder="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} className={`${inputClass} py-1`} />
        {error && <p className="text-xs text-loss">{error}</p>}
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setAdjusting(null)}>
            Annulla
          </Button>
          <Button size="sm" variant="primary" disabled={busy} onClick={() => void save()}>
            Salva
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end">
      <span className={signClass(Number(leg.pnlEffective))}>{money(leg.pnlEffective, leg.currency)}</span>
      <span className="text-xs text-faint">
        gateway <Auto>{money(leg.pnlMt5, leg.currency)}</Auto>
        {adj !== 0 && <span className="text-muted"> · rettifica {money(adj, leg.currency)}</span>}
      </span>
      {canAdjust && (
        <button className="text-xs text-muted hover:text-ink hover:underline" onClick={() => setAdjusting(leg.id)}>
          Rettifica
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parametri
// ---------------------------------------------------------------------------

function ParamsTab({ s, canWrite, onOutcome }: { s: Detail; canWrite: boolean; onOutcome: (k: "complete" | "fail") => void }) {
  const refresh = useRefreshDirectory();
  const live = s.state === "ACTIVE" || s.state === "PAUSED";
  const editable = canWrite && live;
  const [maxOps, setMaxOps] = useState(String(s.maxOpsPerDay));
  const [dailyDd, setDailyDd] = useState(fractionToPct(s.dailyDdPct));
  const [target, setTarget] = useState(fractionToPct(s.challengeTargetPct));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const body = {
      maxOpsPerDay: intInput(maxOps),
      dailyDdPct: dailyDd.trim() ? pctToFraction(dailyDd) : null,
      challengeTargetPct: target.trim() ? pctToFraction(target) : null,
    };
    if (!body.maxOpsPerDay) return setError("Controlla le operazioni al giorno");
    if ((dailyDd.trim() && !body.dailyDdPct) || (target.trim() && !body.challengeTargetPct)) return setError("Le percentuali vanno scritte come numeri, per esempio 3");
    setBusy(true);
    try {
      await api.sessions.update(s.id, body as never);
      await refresh();
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const num = (label: string, value: string, set: (v: string) => void, suffix?: string, hint?: string) => (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="relative">
          <input id={id} inputMode="decimal" disabled={!editable} value={value} onChange={(e) => set(e.target.value)} className={`${inputClass} ${suffix ? "pr-10" : ""}`} />
          {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
        </div>
      )}
    </Field>
  );

  const c = s.cycle;
  // Accumulatori e recover del ciclo: valuta del ciclo, cioè del conto prop.
  const cur = s.cycle.currency;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={save} className="space-y-4">
        <h2 className="text-sm font-medium text-ink">Limiti della fase</h2>
        <p className="text-sm text-muted">
          Lotto, moltiplicatore, stop loss e take profit non stanno qui: si scelgono a ogni operazione, nella scheda Operazione.
        </p>
        {!live && <p className="text-sm text-muted">Fase chiusa: i limiti restano come riferimento e non si modificano più.</p>}
        <div className="grid grid-cols-2 gap-3">
          {num("Operazioni massime al giorno", maxOps, setMaxOps)}
          {num("DD giornaliero", dailyDd, setDailyDd, "%")}
          {num("Target di fase", target, setTarget, "%")}
        </div>
        <ErrorNote>{error}</ErrorNote>
        {editable && (
          <div className="flex items-center gap-3">
            <Button variant="primary" type="submit" disabled={busy}>
              {busy ? "Salvataggio…" : "Salva i limiti"}
            </Button>
            {saved && <span className="text-sm text-gain">Salvati.</span>}
          </div>
        )}
      </form>

      <div className="space-y-4">
        <h2 className="text-sm font-medium text-ink">Ciclo</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-card border border-line bg-surface p-4 text-sm">
          <Stat label="Etichetta">{c.label ?? <Dash />}</Stat>
          <Stat label="Fase del ciclo">{PHASE_LABEL(c.currentPhase)}</Stat>
          <Stat label="Taglia">{money(c.accountSize, s.propAccount.currency)}</Stat>
          <Stat label="Max DD">{`${percent(c.maxDdPct)} · ${LABELS.ddBasis[c.maxDdBasis]}`}</Stat>
          <Stat label={LABELS.terms.feesPaid}>
            <FeesPaid value={c.feesPaid} currency={cur} info={s.feesPaidInfo} />
          </Stat>
          <Stat label={LABELS.terms.realRealizedPnl}>
            <span className={signClass(Number(c.realRealizedPnl))}>{money(c.realRealizedPnl, cur)}</span>
          </Stat>
          <Stat label={LABELS.terms.adjustments}>{money(c.adjustments, cur)}</Stat>
          <Stat label="Profitto desiderato">{money(c.desiredNetProfit, cur)}</Stat>
          <Stat label={`${LABELS.terms.recoverAmount} (ciclo)`}>{money(c.recoverAmount, cur)}</Stat>
          <Stat label="Recover all'avvio della fase">{money(s.profitTarget, cur)}</Stat>
          {s.lastOperation && (
            <Stat label="Ultima operazione">
              {`n. ${s.lastOperation.seq} · ${Number(s.lastOperation.propLots)} lotti · x${Number(s.lastOperation.brokerMultiplier)} · SL ${s.lastOperation.slPoints} pt`}
            </Stat>
          )}
        </dl>
        {canWrite && live && (
          <div className="flex flex-wrap gap-2">
            <PauseButton s={s} />
            <Button onClick={() => onOutcome("complete")}>{LABELS.sessionState.COMPLETED}</Button>
            <Button onClick={() => onOutcome("fail")}>{LABELS.sessionState.FAILED}</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}
